import { Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectedComponent } from "../selected/selected.component";
import { InputComponent } from "../input/input.component";
import { CommonModule } from '@angular/common';
import { ListsService } from '../../services/lists.service';
import { AUTOMATIC_DEBIT_INPUTS, AUTOMATIC_DEBIT_SELECTORS } from 'app/config/constants.config';
import { LockToggleComponent } from '../lock-toggle/lock-toggle.component';
import { FieldErrorTimeoutService } from '../../services/field-error-timeout.service';

@Component({
  selector: 'app-automatic-debit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectedComponent,
    InputComponent,
    LockToggleComponent
  ],
  templateUrl: './automatic-debit.component.html',
  styleUrl: './automatic-debit.component.css'
})
export class AutomaticDebitComponent implements OnInit, OnChanges {

  form: FormGroup;
  isTimeout: boolean = false;
  showDebitDetails: boolean = false;
  private previousAutomaticDebitValue: boolean | null = null;
  private isUpdating = false;
  private originalValues: any = {};
  automaticDebitUnlocked = true;
  private debitLockTouched = false;

  dataInputs = AUTOMATIC_DEBIT_INPUTS;
  dataSelected = AUTOMATIC_DEBIT_SELECTORS;

  @Output() formValueChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() defaultValue: any;
  @Output() validData = new EventEmitter<any>();
  @Input() isCheckData = false;
  @Input() dataLastOnboarding: any;

  constructor(
    private _listService: ListsService,
    private fieldErrorTimeout: FieldErrorTimeoutService,
    private cdr: ChangeDetectorRef
  ){
    this.form = new FormGroup({
      automaticDebit: new FormControl(null, [Validators.required]),
      debitType: new FormControl(null),
      bank: new FormControl(null),
      accountNumber: new FormControl(null),
      accountType: new FormControl(null),
      debitDay: new FormControl(null)
    });

    this.form.valueChanges.subscribe(() => {
      if (!this.isUpdating) {
        setTimeout(() => {
          this.emitForm();
        }, 1300);
      }
    });

    this.form.get('debitType')?.valueChanges.subscribe((value) => {
      if (value === 'EXTERNO') {
        this.enableExternalFields();
      } else {
        this.disableExternalFields();
      }
    });

    this.form.get('automaticDebit')?.valueChanges.subscribe((value) => {
      this.setAutomaticDebit();
    });
  }

  ngOnInit(): void {
    this.getAllBanks();
    this.loadDefaultData();
    this.scheduleRequiredTimeout();
  }

  private scheduleRequiredTimeout(): void {
    setTimeout(() => {
      if (this.form.get('automaticDebit')?.invalid) {
        this.isTimeout = true;
        this.cdr.detectChanges();
      }
    }, this.fieldErrorTimeout.delayMs(8));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultValue'] && changes['defaultValue'].currentValue) {
      this.loadDefaultData();
    }
  }

  private loadDefaultData(): void {
    if (!this.defaultValue) return;

    this.isUpdating = true;
    this.originalValues = { ...this.defaultValue };

    this.form.patchValue({
      automaticDebit: this.defaultValue?.automaticDebit,
      debitType: this.defaultValue?.debitType,
      bank: this.defaultValue?.bank,
      accountNumber: this.defaultValue?.accountNumber,
      accountType: this.defaultValue?.accountType,
      debitDay: this.defaultValue?.debitDay
    }, { emitEvent: false });

    this.previousAutomaticDebitValue = this.defaultValue.automaticDebit;
    if (!this.debitLockTouched) {
      this.automaticDebitUnlocked = true;
      this.form.get('automaticDebit')?.enable({ emitEvent: false });
    }

    if (this.defaultValue.automaticDebit === true) {
      this.showDebitDetails = true;
      this.enableDebitFields();

      if (this.defaultValue.debitType === 'EXTERNO') {
        this.enableExternalFields();
      }
    }

    // Actualizar configuraciones con estado original
    this.dataInputs.update((inputs: any) =>
      inputs.map((input: any) => ({
        ...input,
        defaultValue: this.defaultValue[input.formName] ?? input.defaultValue,
        originalValue: this.defaultValue[input.formName] ?? input.defaultValue,
        isDirty: false,
        isValid: true,
        enabled: true,
        showBlocker: true
      }))
    );

    this.dataSelected.update((selects: any) =>
      selects.map((select: any) => ({
        ...select,
        defaultValue: this.defaultValue[select.formName] ?? select.defaultValue,
        originalValue: this.defaultValue[select.formName] ?? select.defaultValue,
        isDirty: false,
        isValid: true,
        enabled: true,
        showBlocker: true
      }))
    );

    setTimeout(() => {
      this.isUpdating = false;
    }, 200);
  }

  toggleAutomaticDebitLock(): void {
    this.debitLockTouched = true;
    this.automaticDebitUnlocked = !this.automaticDebitUnlocked;
    const control = this.form.get('automaticDebit');
    if (this.automaticDebitUnlocked) {
      control?.enable({ emitEvent: false });
    } else {
      control?.disable({ emitEvent: false });
    }
  }

  shouldShowDebitDetails(): boolean {
    if (this.isCheckData && this.dataLastOnboarding?.automaticDebit === true) {
      return true;
    }
    return this.showDebitDetails;
  }

  shouldShowExternalDebitFields(): boolean {
    const debitType = this.form.get('debitType')?.value;

    if (this.isCheckData && this.dataLastOnboarding?.debitType === 'EXTERNO') {
      return true;
    }
    return debitType === 'EXTERNO';
  }

  getAccountTypeLabel(value: string): string {
    const accountTypes: {[key: string]: string} = {
      'Ahorros': 'Cuenta de Ahorros',
      'Corriente': 'Cuenta Corriente'
    };
    return accountTypes[value] || value;
  }

  setAutomaticDebit(): void {
    if (this.isUpdating) return;

    const wantsAutomaticDebit = this.form.get('automaticDebit')?.value;

    if (wantsAutomaticDebit === true) {
      this.showDebitDetails = true;
      this.enableDebitFields();
    } else {
      this.showDebitDetails = false;
      this.disableAllDebitFields();

      this.form.patchValue({
        debitType: null,
        bank: null,
        accountNumber: null,
        accountType: null,
        debitDay: null
      }, { emitEvent: false });
    }

    this.previousAutomaticDebitValue = wantsAutomaticDebit;
  }

  private enableDebitFields(): void {
    this.form.get('debitType')?.setValidators(Validators.required);
    this.form.get('debitType')?.updateValueAndValidity();
    this.updateFieldEnabledStatus('debitType', true, 0);
  }

  private disableAllDebitFields(): void {
    ['debitType', 'bank', 'accountNumber', 'accountType', 'debitDay'].forEach(field => {
      this.form.get(field)?.clearValidators();
      this.form.get(field)?.updateValueAndValidity();
      this.updateFieldEnabledStatus(field, false);
    });
  }

  getAllBanks(){
    this._listService.getAllBanks().subscribe({
      next: (response: any) => {
        this.dataSelected().at(1)?.options.set(response);
      },
      error: (error: any) => {
        console.error("ERROR: ", error)
      }
    })
  }

  emitForm(){
    const wantsAutomaticDebit = this.form.get('automaticDebit')?.value;
    const debitType = this.form.get('debitType')?.value;
    const isFormValid = this.form.valid;

    const emitData: any = {
      _isValid: isFormValid,
      _formName: 'automaticDebit'
    };

    // Si el valor de automaticDebit cambió, emitir inmediatamente
    if (wantsAutomaticDebit !== this.previousAutomaticDebitValue) {
      if (wantsAutomaticDebit === false) {
        this.formValueChange.emit({
          automaticDebit: false,
          debitType: null,
          bank: null,
          accountNumber: null,
          accountType: null,
          debitDay: null,
          _isValid: true,
          _formName: 'automaticDebit'
        });
      } else if (wantsAutomaticDebit === true) {
        this.formValueChange.emit({
          automaticDebit: true,
          debitType: null,
          bank: null,
          accountNumber: null,
          accountType: null,
          debitDay: null,
          _isValid: true,
          _formName: 'automaticDebit'
        });
      }
      this.previousAutomaticDebitValue = wantsAutomaticDebit;
      return;
    }

    // Caso 1: No quiere débito automático
    if (wantsAutomaticDebit === false) {
      this.formValueChange.emit({
        automaticDebit: false,
        _isValid: true,
        _formName: 'automaticDebit'
      });
      return;
    }

    // Caso 2: Quiere débito automático pero no ha seleccionado tipo
    if (wantsAutomaticDebit === true && !debitType) {
      this.formValueChange.emit({
        automaticDebit: true,
        _isValid: false,
        _formName: 'automaticDebit'
      });
      return;
    }

    // Caso 3: Débito interno - emitir los 2 campos
    if (wantsAutomaticDebit === true && debitType === 'INTERNO') {
      this.formValueChange.emit({
        automaticDebit: true,
        debitType: 'INTERNO',
        bank: null,
        accountNumber: null,
        accountType: null,
        debitDay: null,
        _isValid: true,
        _formName: 'automaticDebit'
      });
      return;
    }

    // Caso 4: Débito externo - verificar que todos los campos estén válidos
    if (wantsAutomaticDebit === true && debitType === 'EXTERNO') {
      const formValue = { ...this.form.value };
      this.formValueChange.emit({
        ...formValue,
        _isValid: this.form.valid,
        _formName: 'automaticDebit'
      });
      return;
    }
  }

  handleValue(event: { formName: string, value: string | number, isValid?: boolean, isDirty?: boolean }) {
    this.isUpdating = true;
    this.form.get(event.formName)?.setValue(event.value);

    const formValue = { ...this.form.value };
    this.formValueChange.emit({
      ...formValue,
      _isValid: this.form.valid,
      _formName: 'automaticDebit',
      _isDirty: event.isDirty || false
    });

    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  private enableExternalFields(): void {
    this.form.get('bank')?.setValidators(Validators.required);
    this.form.get('accountNumber')?.setValidators(Validators.required);
    this.form.get('accountType')?.setValidators(Validators.required);
    this.form.get('debitDay')?.setValidators([Validators.required, Validators.min(1), Validators.max(31)]);

    this.form.get('bank')?.updateValueAndValidity();
    this.form.get('accountNumber')?.updateValueAndValidity();
    this.form.get('accountType')?.updateValueAndValidity();
    this.form.get('debitDay')?.updateValueAndValidity();

    this.updateFieldEnabledStatus('bank', true);
    this.updateFieldEnabledStatus('accountNumber', true);
    this.updateFieldEnabledStatus('accountType', true);
    this.updateFieldEnabledStatus('debitDay', true);
  }

  private disableExternalFields(): void {
    this.form.get('bank')?.clearValidators();
    this.form.get('accountNumber')?.clearValidators();
    this.form.get('accountType')?.clearValidators();
    this.form.get('debitDay')?.clearValidators();

    this.form.patchValue({
      bank: null,
      accountNumber: null,
      accountType: null,
      debitDay: null
    }, { emitEvent: false });

    this.form.get('bank')?.updateValueAndValidity();
    this.form.get('accountNumber')?.updateValueAndValidity();
    this.form.get('accountType')?.updateValueAndValidity();
    this.form.get('debitDay')?.updateValueAndValidity();

    this.updateFieldEnabledStatus('bank', false);
    this.updateFieldEnabledStatus('accountNumber', false);
    this.updateFieldEnabledStatus('accountType', false);
    this.updateFieldEnabledStatus('debitDay', false);
  }

  private updateFieldEnabledStatus(formName: string, enabled: boolean, index?: number): void {
    const selectedIndex = index !== undefined ? index : this.dataSelected().findIndex(item => item.formName === formName);
    if (selectedIndex !== -1) {
      const updatedSelected = [...this.dataSelected()];
      updatedSelected[selectedIndex] = { ...updatedSelected[selectedIndex], enabled };
      this.dataSelected.set(updatedSelected);
    }

    const inputIndex = this.dataInputs().findIndex(item => item.formName === formName);
    if (inputIndex !== -1) {
      const updatedInputs = [...this.dataInputs()];
      updatedInputs[inputIndex] = { ...updatedInputs[inputIndex], enabled };
      this.dataInputs.set(updatedInputs);
    }
  }
}
